import { Modal, Tabs } from "@shared/ui";
import type { TabsProps } from "antd";
import { Flex } from "antd";
import { BarChart3, CheckCircle2, Users, X } from "lucide-react";
import {
  AiFitAnalysis,
  getRecommendationDisplay,
  getScoreColor,
} from "../lib/aiFitCheckUtils";
import { DetailsTab } from "./DetailsTab";
import { OverviewTab } from "./OverviewTab";
import { ScoresTab } from "./ScoresTab";
import styles from "./styles.module.scss";

interface AiFitReportModalProps {
  open: boolean;
  onClose: () => void;
  analysis: AiFitAnalysis;
}

export function AiFitReportModal({
  open,
  onClose,
  analysis,
}: AiFitReportModalProps) {
  const fit = analysis.creator_campaign_fit;
  const { label, color, bg } = getRecommendationDisplay(
    fit?.recommendation,
    fit?.verdict
  );

  const tabItems: TabsProps["items"] = [
    {
      key: "overview",
      label: (
        <span className={styles.tabLabel}>
          <CheckCircle2 size={14} />
          Overview
        </span>
      ),
      children: <OverviewTab data={analysis} />,
    },
    {
      key: "scores",
      label: (
        <span className={styles.tabLabel}>
          <BarChart3 size={14} />
          Scores
        </span>
      ),
      children: <ScoresTab data={analysis} />,
    },
    {
      key: "details",
      label: (
        <span className={styles.tabLabel}>
          <Users size={14} />
          Details
        </span>
      ),
      children: <DetailsTab data={analysis} />,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={null}
      closable={false}
      // closable={false}
      className={styles.reportModal}
    >
      <div
        className={styles.modalInner}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className={styles.modalHeader}>
          <Flex justify="space-between" align="flex-start" gap={16}>
            <div className={styles.headerMain}>
              <Flex
                wrap="wrap"
                align="center"
                gap={8}
                className={styles.headerPills}
              >
                {fit?.verdict ? (
                  <span
                    className={styles.verdictPill}
                    style={{ color, background: bg }}
                  >
                    {fit.verdict}
                  </span>
                ) : null}
                <span className={styles.recommendationPill}>{label}</span>
              </Flex>
              <h2 className={styles.creatorName}>
                {fit?.creator_name || "Creator"}
              </h2>
              {fit?.one_line_summary ? (
                <p className={styles.oneLine}>{fit.one_line_summary}</p>
              ) : null}
            </div>
            <Flex align="flex-start" gap={12} className={styles.headerRight}>
              <div className={styles.scoreBox}>
                <span
                  className={styles.scoreBoxValue}
                  style={{ color: getScoreColor(fit?.overall_score) }}
                >
                  {fit?.overall_score ?? "-"}
                </span>
                <span className={styles.scoreBoxLabel}>Score</span>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </Flex>
          </Flex>
        </div>

        <Tabs defaultActiveKey="overview" items={tabItems} />
      </div>
    </Modal>
  );
}
